<template>
  <div class="d-flex flex-column justify-start px-6">
    <div class="d-flex justify-space-between align-center mt-6">
      <p class="h6">
        {{ $t('ai_features.usage.dashboard_title') }}
      </p>

      <Button
        color="primary"
        icon="mdi-creation-outline"
        outlined
        size="small"
        :href="
          `mailto:csmteam@reverscore.com?subject=${$t(
            'ai_features.usage.request_tokens_mail_subject'
          )}&body=${$t('ai_features.usage.request_tokens_mail_body', {
            accountName: this.accountName,
            organizationName: this.organizationName,
          })}`
        "
        :text="$t('ai_features.request_more_tokens')"
      />
    </div>

    <TokenUsagePanel class="mt-2 mb-6" />

    <v-card outlined class="mt-4 mb-6">
      <v-data-table
        item-key="month"
        show-expand
        hide-default-footer
        class="tokens-usage-table"
        :headers="monthlyUsageHeaders"
        :items="usageByMonth"
        :single-expand="singleExpand"
        :expanded.sync="expanded"
        :loading="isLoading"
        :loading-text="$t('ai_features.loading_tokens_usage')"
        :item-class="handleClass"
      >
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="nested-table">
            <v-data-table
              hide-default-footer
              hide-default-header
              disable-pagination
              :headers="userUsageHeaders"
              :items="item.breakdownByUser"
            >
              <template v-slot:[`item.user`]="{ item }">
                <td style="width: 250px;">
                  <ChipAvatar
                    small
                    :account="item"
                    :text-color="'blue-grey--text text--darken-2'"
                  />
                </td>
              </template>

              <template v-slot:[`item.tokensUsageText`]="{ item }">
                <td>
                  <div class="d-flex align-center justify-start">
                    <div
                      v-if="Boolean(item.percentageUsage)"
                      class="percentage-bar"
                    >
                      <v-progress-linear
                        class="d-flex align-center justify-center"
                        height="25"
                        :color="getBarColor(item.percentageUsage)"
                        :value="item.percentageUsage"
                      >
                        <div class="d-flex align-center justify-center">
                          <span class="caption ma-0 pa-0">
                            {{ `${item.percentageUsage}%` }}</span
                          >
                        </div>
                      </v-progress-linear>
                    </div>

                    <span class="ml-2 caption blue-grey--text text--darken-1">{{
                      item.tokensUsageText
                    }}</span>
                  </div>
                </td>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import TokenUsagePanel from '@/components/AIManagement/TokensUsagePanel.vue'
import Button from '@/components/UI/Button'
import ChipAvatar from '@/components/ChipAvatar.vue'

import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import api from '@/api'

const DEFAULT_LANGUAGE = 'en-US'
const UNLIMITED_TOKEN_LIMIT = -1

export default {
  name: 'UsageTab',
  mixins: [mixin],

  components: {
    TokenUsagePanel,
    Button,
    ChipAvatar,
  },

  data() {
    return {
      setLimitsLoading: false,
      expanded: [],
      singleExpand: true,
      monthlyUsageHeaders: [
        { text: '', value: 'data-table-expand', width: '52px' },
        {
          text: this.$t('ai_features.usage.table_month'),
          value: 'month',
          align: 'start',
          sortable: false,
          width: '250px',
        },
        {
          text: this.$t('ai_features.usage.table_tokens_used'),
          value: 'totalTokensConsumed',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$t('ai_features.usage.table_active_users'),
          value: 'activeUsers',
          align: 'start',
          sortable: false,
        },
      ],
      userUsageHeaders: [{ value: 'user' }, { value: 'tokensUsageText' }],
      usageByMonth: [],
      isLoading: false,
    }
  },

  async mounted() {
    await this.fetchOrganizationAISettings()
    this.fetchTokensUsage()
  },

  computed: {
    ...mapGetters(['account', 'organization', 'organizationId']),

    organizationName() {
      return this.organization?.name || ''
    },

    language() {
      return window.navigator.language || DEFAULT_LANGUAGE
    },

    accountName() {
      return this.getFullName(this.account) || ''
    },
  },

  methods: {
    handleClass(item) {
      if (!Array.isArray(this.expanded) || !item?.value) return ''
      return this.expanded.some(
        expandedItem => expandedItem.value === item.value
      )
        ? 'expanded-class'
        : ''
    },

    async fetchOrganizationAISettings() {
      const {
        data: organizationAISettings,
      } = await api.ai.getOrganizationAISettings(this.organizationId)

      this.organizationAISettings = organizationAISettings
    },

    getBarColor(percentageUsed) {
      if (percentageUsed == null || percentageUsed === '') return ''
      if (percentageUsed <= 69) return 'green lighten-1'
      if (percentageUsed >= 70 && percentageUsed <= 79)
        return 'orange lighten-1'
      if (percentageUsed >= 80 && percentageUsed <= 89)
        return 'yellow lighten-1'
      return 'red lighten-1'
    },

    getMonthText(month) {
      const date = new Date()
      date.setDate(1)
      date.setMonth(month - 1)

      return date.toLocaleDateString(this.language, { month: 'long' })
    },

    formatNumberToText(value) {
      return Intl.NumberFormat(this.language, {
        maximumFractionDigits: 1,
      }).format(value)
    },

    async fetchTokensUsage() {
      try {
        this.isLoading = true

        const { data } = await api.ai.getTokensUsage(
          this.organizationId,
          new Date().getFullYear()
        )

        this.usageByMonth = data.map(item => ({
          ...item,
          month: this.getMonthText(item.month),
          totalTokensConsumed: this.formatNumberToText(
            item.totalTokensConsumed || 0
          ),
          activeUsers: item.breakdownByUser.length ?? 0,
          value: item.month,
          breakdownByUser: item.breakdownByUser?.map(user =>
            this.parseUserBreakdownData(user)
          ),
        }))
      } catch (error) {
        this.showError(this.$t('ai_features.usage.fetch_error'))
      } finally {
        this.isLoading = false
      }
    },

    parseUserBreakdownData(userData) {
      const totalTokensUsed = userData?.totalTokensConsumed || 0
      const userMonthlyLimit =
        userData?.aiMonthlyTokenLimit ??
        this.organizationAISettings.usersTokensMonthlyLimit ??
        0

      return {
        ...userData,
        percentageUsage: this.calculateUserPercentageUsage(
          totalTokensUsed,
          userMonthlyLimit
        ),
        tokensUsageText: this.getUserTokensUsageText(
          userData,
          this.organizationAISettings.data
        ),
      }
    },

    calculateUserPercentageUsage(tokensUsed, monthlyLimit) {
      if (monthlyLimit === UNLIMITED_TOKEN_LIMIT) return 0
      if (monthlyLimit <= 0) return 0

      return Number(((tokensUsed / monthlyLimit) * 100).toFixed(1))
    },

    getUserTokensUsageText(user) {
      if (user?.aiMonthlyTokenLimit === UNLIMITED_TOKEN_LIMIT) {
        return this.$t('ai_features.usage.tokens_used_per_unlimited_user', {
          tokensUsed: this.formatNumberToText(user.totalTokensConsumed),
        })
      }

      return this.$t('ai_features.usage.tokens_used_per_user', {
        tokensUsed: this.formatNumberToText(user.totalTokensConsumed),
        tokenLimit: this.formatNumberToText(
          user?.aiMonthlyTokenLimit ??
            this.organizationAISettings.usersTokensMonthlyLimit
        ),
      })
    },
  },
}
</script>
<style lang="scss">
.tokens-usage-table {
  > .v-data-table__wrapper {
    tbody {
      tr.v-data-table__expanded__content {
        box-shadow: none;
        background-color: transparent;
        border: none;
        pointer-events: none;
        &:hover {
          background-color: transparent !important;
        }
      }
    }
  }
}

.nested-table {
  padding-left: 52px !important;
}

.expanded-class {
  background-color: $color-blue-grey-lighten3 !important;
  font-weight: 500 !important;
}

.percentage-bar {
  width: 100px;
  margin-left: 8px;
}
</style>
